import React from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import { Button } from "@/components/ui/button";
import { Link } from 'react-router-dom';
import { ArrowLeft, Cookie, Settings, Info } from "lucide-react";

export default function CookiePolicy() {
  return (
    <div className="min-h-screen bg-background font-sans antialiased">
      <Navbar />
      
      <main className="pt-24 pb-20">
        {/* Header */}
        <div className="container mx-auto px-6 mb-12">
          <div className="mb-8">
            <Button variant="ghost" asChild className="gap-2 pl-0 hover:pl-2 transition-all">
              <Link to="/">
                <ArrowLeft className="h-4 w-4" /> Back to Home
              </Link>
            </Button>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-primary mb-6">
            Cookie <span className="text-secondary">Policy</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl">
            This policy explains how WorkRidePro uses cookies and similar technologies to recognize you when you visit our website or use our app.
          </p>
          <p className="text-sm text-muted-foreground mt-4">
            Last Updated: November 28, 2024
          </p>
        </div>

        {/* Content */}
        <div className="container mx-auto px-6 grid lg:grid-cols-3 gap-12">
          
          {/* Main Text */}
          <div className="lg:col-span-2 space-y-12">
            
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-primary">1. What are Cookies?</h2>
              <p className="text-muted-foreground leading-relaxed">
                Cookies are small data files that are placed on your computer or mobile device when you visit a website. Cookies are widely used by website owners in order to make their websites work, or to work more efficiently, as well as to provide reporting information.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-primary">2. Why We Use Cookies</h2>
              <p className="text-muted-foreground leading-relaxed">
                We use cookies for several reasons:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li><strong>Essential Cookies:</strong> These are strictly necessary to provide you with services available through our website and to use some of its features, such as access to secure areas.</li>
                <li><strong>Performance & Functionality Cookies:</strong> These are used to enhance the performance and functionality of our website but are non-essential to their use. However, without these cookies, certain functionality may become unavailable.</li>
                <li><strong>Analytics & Customization Cookies:</strong> These collect information that is used either in aggregate form to help us understand how our website is being used or how effective our marketing campaigns are.</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-primary">3. How to Control Cookies</h2>
              <p className="text-muted-foreground leading-relaxed">
                You have the right to decide whether to accept or reject cookies. You can exercise your cookie rights by setting your preferences in the Cookie Consent Manager. In addition, most web browsers allow you to control cookies through their settings preferences.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-primary">4. Updates to This Policy</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may update this Cookie Policy from time to time in order to reflect, for example, changes to the cookies we use or for other operational, legal, or regulatory reasons. Please therefore re-visit this Cookie Policy regularly to stay informed about our use of cookies and related technologies.
              </p>
            </section>

          </div>

          {/* Sidebar / Highlights */}
          <div className="lg:col-span-1">
            <div className="bg-slate-50 dark:bg-slate-900 p-8 rounded-2xl border border-border sticky top-24">
              <h3 className="text-xl font-bold text-primary mb-6">Cookie Types</h3>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Cookie className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground">Essential</h4>
                    <p className="text-sm text-muted-foreground">Required for the site to function.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Settings className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground">Functional</h4>
                    <p className="text-sm text-muted-foreground">Remembers your preferences.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <Info className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground">Analytics</h4>
                    <p className="text-sm text-muted-foreground">Helps us improve our service.</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-border">
                <p className="text-sm font-medium text-foreground mb-2">Questions about cookies?</p>
                <a href="mailto:privacy@workridepro.com" className="text-secondary font-bold hover:underline">
                  Contact Privacy Team
                </a>
              </div>
            </div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
}
